import debounce from "lodash/debounce";
import moment from "moment";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import sdk from "../../sdk/sdk";
import { round2 } from "../../utils";
import { getPricesSteps } from "../../utils_price";
import * as actions from "./actions";

export function useCart(id) {
    const dispatch = useDispatch();

    const mergeCarts = (data) => {
        let carts = [];
        let current_cart = {};
        if (data) {
            Object.values(data).map((el) => {
                if (!el) return;
                if (el?.carts && Array.isArray(el.carts)) {
                    carts = [...carts, ...el.carts];
                }
                current_cart = el?.current_cart || {};
            });
        }
        return { carts, current_cart };
    };

    const { carts, isCartsLoaded } = useSelector((state) => ({
        isCartsLoaded: state.carts.isCartsLoaded,
        carts: id ? state.carts[id] : mergeCarts(state.carts),
    })) || { carts: [], current_cart: {}, isCartsLoaded: false };

    const debouncedSyncRef = useRef();

    useEffect(() => {
        if (id) {
            const debounceFn = debounce(
                async ({ products = {}, packs = [], carts }) => {
                    try {
                        const header = {
                            ...(carts.current_cart.header || {}),
                            id: carts.current_cart._id,
                            CODCLI: carts.current_cart.client.CODCLI,
                        };

                        const current_cart = {
                            header,
                            products: { ...carts.current_cart.products, ...products },
                            packs: packs,
                        };

                        const order = generateOrder(current_cart);
                        order.header = header;
                        order.current_cart = {
                            _id: carts.current_cart._id,
                            created_at: carts.current_cart.created_at,
                            products: current_cart.products,
                            packs: current_cart.packs,
                        };
                        await sdk.orders.updateCartDB(order);
                        console.log("✅ Cart saved successfully", {
                            cartId: order.header.id,
                            productCount: order.items.length,
                        });
                    } catch (error) {
                        console.error("❌ Failed to sync cart to database", error);
                        // Show user-visible error
                        alert(
                            "Erreur: Impossible de sauvegarder le panier. Vérifiez votre connexion.",
                        );
                    }
                },
                500, // Reduced from 1500ms to minimize data loss on navigation
            );

            debouncedSyncRef.current = debounceFn;

            // Add beforeunload handler to flush pending saves
            const handleBeforeUnload = () => {
                if (debouncedSyncRef.current?.flush) {
                    debouncedSyncRef.current.flush();
                }
            };

            window.addEventListener("beforeunload", handleBeforeUnload);

            return () => {
                debounceFn.cancel();
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }
    }, [id]);

    const methods = {
        createCart: ({ client, id, creator, products = {}, packs = [] }) =>
            dispatch({
                type: actions.CREATE_CART,
                payload: { client, id, creator, products, packs },
            }),

        selectCart: (idCart) =>
            dispatch({ type: actions.SELECT_CART, payload: idCart }),

        addInCart: (gen_products) => {
            dispatch({ type: actions.ADD_IN_CART, payload: gen_products });

            if (debouncedSyncRef.current) {
                debouncedSyncRef.current({
                    products: gen_products.product,
                    packs: carts.current_cart.packs || [],
                    carts,
                });
            }
        },

        addPackInCart: (payload) => {
            dispatch({ type: actions.ADD_PACK_IN_CART, payload });

            if (debouncedSyncRef.current) {
                debouncedSyncRef.current({
                    products: carts.current_cart.products || [],
                    packs: payload.packs,
                    carts,
                });
            }
        },

        removePackFromCart: (payload) => {
            dispatch({ type: actions.REMOVE_PACK_FROM_CART, payload });
            if (debouncedSyncRef.current) {
                debouncedSyncRef.current({
                    products: carts.current_cart.products,
                    packs: carts.current_cart.packs.filter(
                        (pack) => pack.name !== payload.packName,
                    ),
                    carts,
                });
            }
        },

        customRemise: (creator, libgen, codart, remise) =>
            dispatch({
                type: actions.CUSTOM_REMISE,
                payload: { creator, libgen, codart, remise },
            }),

        createHeader: (client) =>
            dispatch({ type: actions.CREATE_HEADER, payload: client }),

        changeFeetRemise: (payload) =>
            dispatch({ type: actions.CHANGE_FEET_REMISE, payload }),

        removeOrderHeader: (payload) =>
            dispatch({ type: actions.REMOVE_ORDER_HEADER, payload }),
        removeCart: (payload) => {
            return sdk.orders
                .deletecartByIdDB(payload.creator, payload._id)
                .then((_) => {
                    dispatch({ type: actions.REMOVE_CART, payload });
                })
                .catch((err) => {
                    alert("Impossible de supprimer le panier. Réessayez.");
                    throw err;
                });
        },
        removeAllCart: (payload) => {
            return sdk.orders
                .deleteAllCartDB(payload.creator)
                .then((_) => {
                    dispatch({ type: actions.REMOVE_ALL_CART });
                })
                .catch((err) => {
                    alert(
                        "Erreur lors de la suppression de tous les paniers. Veuillez réessayer.",
                    );
                    throw err;
                });
        },
        removeCartFromCartList: (payload) => {
            sdk.orders
                .deletecartByIdDB(payload.creator, payload._id)
                .then((_) =>
                    dispatch({ type: actions.REMOVE_CART_FROM_CART_LIST, payload }),
                )
                .catch((_) => alert("Impossible de supprimer le panier. Réessayez."));
        },

        getCartData: () =>
            getCartData(
                carts.current_cart?.products,
                carts.current_cart?.packs,
                carts.current_cart?.client,
            ),

        getCartDataByID: (cartId) => {
            if (!cartId) return null;
            const cart = carts.carts.find((c) => c._id === cartId);
            if (!cart) return null;
            return getCartData(cart.products, cart.packs, cart.client);
        },

        getMaxMtPLVOffert: (data) =>
            getMaxMtPLVOffert(data, carts.current_cart?.client),

        generateOrder: (creator, cb) => {
            // Auto‑flush pending sync before generating order
            if (debouncedSyncRef.current?.flush) {
                debouncedSyncRef.current.flush();
            }

            const order = generateOrder(carts.current_cart);

            sdk.orders
                .create(order)
                .then((data) => {
                    if (data.success) {
                        dispatch({
                            type: actions.GENERATE_ORDER_SUCCESS,
                            payload: {
                                creator,
                                generated_at: moment().format("DD/MM/YYYY"),
                                email: data.payload.mail_sent?.email || "email non renseigné",
                            },
                        });
                    }
                    if (!data.success && data?.error_type === "CART_ALREADY_PLACED") {
                        throw new Error(data?.status_msg);
                    }

                    if (cb) cb(null, data.payload);
                })
                .catch((error) => {
                    console.error(error);
                    if (cb) cb(error, null);
                });
        },

        changeHeader: (key, value) =>
            dispatch({
                type: actions.CHANGE_CART_HEADER,
                payload: {
                    creator: id,
                    key,
                    value,
                },
            }),

        flushSyncCartToDB: () => {
            if (debouncedSyncRef.current?.flush) {
                debouncedSyncRef.current.flush();
            }
        },

        replaceCartId: (oldId, newId, id) => {
            dispatch({
                type: actions.REPLACE_CART_ID,
                payload: { creator: id, oldId: oldId, newId: newId },
            });

            // If the current cart is the one being replaced, update it too
            if (carts.current_cart._id === oldId) {
                dispatch({
                    type: actions.CHANGE_CART_HEADER,
                    payload: {
                        creator: id,
                        key: "_id",
                        value: newId,
                    },
                });
            }
        },

        syncCartDataByLanguage: (id, carts, current_cart) =>
            dispatch({
                type: actions.UPDATE_CART_AS_LANG,
                payload: { creator: id, carts: carts, current_cart: current_cart },
            }),
    };

    return [carts, methods, isCartsLoaded];
}

function getCartData(products, packs, client) {
    const mt_total_remise_pack = packs?.reduce(
        (acc, pack) => acc + pack.qte_commande * pack.mt_remise,
        0,
    );

    const results = Object.keys(products || {}).reduce(
        (acc, genName) => {
            let articles = products[genName];
            articles.forEach((art) => {
                const pub = art.article.PUB;
                const nbUnit = art?.article?.PUAu_Vente || 1;
                const remise = (art.custom_remise || 0) / 100;

                let itemTotalPrice = 0;

                if (art.article.prices) {
                    const { priceStep, nbUnit: stepNbUnit } = getPricesSteps(
                        art.article,
                        art.qte_commande,
                    );
                    if (priceStep) {
                        const netUnitPrice = round2(
                            (priceStep.price / stepNbUnit) * (1 - remise),
                        );
                        itemTotalPrice = netUnitPrice * art.qte_commande;
                    } else {
                        const netUnitPrice = round2((pub / nbUnit) * (1 - remise));
                        itemTotalPrice = netUnitPrice * art.qte_commande;
                    }
                } else {
                    const netUnitPrice = round2((pub / nbUnit) * (1 - remise));
                    itemTotalPrice = netUnitPrice * art.qte_commande;
                }

                itemTotalPrice = round2(itemTotalPrice);

                if (art.article.gen_in_cumul === "O") {
                    acc.cumul_ca_plv += itemTotalPrice;
                }

                if (art.article.TESTEUR_ou_PLV !== "") {
                    acc.cumul_plv_in_cart += itemTotalPrice;
                }

                acc.mt_total += itemTotalPrice;
            });
            //
            return acc;
        },
        {
            cumul_ca_plv: 0,
            mt_total: 0,
            cumul_plv_in_cart: 0,
            mt_total_remise_pack: mt_total_remise_pack,
        },
    );

    const finalResults = {
        cumul_ca_plv: round2(results.cumul_ca_plv),
        mt_total: round2(results.mt_total),
        cumul_plv_in_cart: round2(results.cumul_plv_in_cart),
        mt_total_remise_pack: round2(results.mt_total_remise_pack),
    };

    const mt_plv_offert = getMaxMtPLVOffert(finalResults, client);
    finalResults.remise_plv =
        finalResults.cumul_plv_in_cart >= mt_plv_offert
            ? mt_plv_offert
            : results.cumul_plv_in_cart;

    return finalResults;
}

const getMaxMtPLVOffert = (data, client) => {
    if (!client) return 0;
    //
    let mt_remise = 0;
    if (data.cumul_ca_plv > client.groupe_tarifaire.MT_CDC_SEUIL4) {
        mt_remise =
            data.cumul_ca_plv * (client.groupe_tarifaire.REMISE_PLV_TESTEUR4 / 100);
    } else if (data.cumul_ca_plv > client.groupe_tarifaire.MT_CDC_SEUIL3) {
        mt_remise =
            data.cumul_ca_plv * (client.groupe_tarifaire.REMISE_PLV_TESTEUR3 / 100);
    } else if (data.cumul_ca_plv > client.groupe_tarifaire.MT_CDC_SEUIL2) {
        mt_remise =
            data.cumul_ca_plv * (client.groupe_tarifaire.REMISE_PLV_TESTEUR2 / 100);
    } else if (data.cumul_ca_plv > client.groupe_tarifaire.MT_CDC_SEUIL1) {
        mt_remise =
            data.cumul_ca_plv * (client.groupe_tarifaire.REMISE_PLV_TESTEUR1 / 100);
    }

    return Math.round(mt_remise * 100) / 100;
};

const generateOrderHeader = (header, cartData) => {
    const baseHeader = {
        CODCLI: header.CODCLI,
        PROSPECT: header.PROSPECT || "N",
        MT_REMISE:
            parseFloat(header.MT_REMISE || 0) + parseFloat(cartData.remise_plv || 0),
        MT_PORT: header.MT_PORT || 0,
        CODVRP: header.CODVRP,
        REF_CLI: header.REF_CLI,
        COMMENTAIRE_LIBRE: header.COMMENTAIRE_LIBRE,
        CODADR_LIV: header.CODADR_LIV,
        CODADR_FAC: header.CODADR_FAC,
        DATE_LIV: header.DATE_LIV,
    };

    if (header.PROSPECT !== "O") {
        return baseHeader;
    }

    // Build header for a PROSPECT
    return {
        ...baseHeader,
        PROSPECT_EMAIL: header.PROSPECT_EMAIL,
        PROSPECT_ADR_LIV: header.PROSPECT_ADR_LIV,
        PROSPECT_ADR_FAC: header.PROSPECT_ADR_FAC || header.PROSPECT_ADR_FACT,
    };
};

const generateOrderItems = (products, packs) => {
    return Object.keys(products).reduce((items, genName) => {
        const genProducts = products[genName];
        const Pack = packs.find((p) => p.name === genName);
        const isPack = !!Pack;

        genProducts.forEach((product) => {
            // Determine REMISE
            const remise =
                product.custom_remise >= 0
                    ? product.custom_remise
                    : product.article.REMISE_FIXE > 0
                        ? product.article.REMISE_FIXE
                        : product.article.groupe_tarifaire.REMISE_FIXE > 0
                            ? product.article.groupe_tarifaire.REMISE_FIXE
                            : 0;

            items.push({
                CODART: product.article.CODART,
                PUB: product.pub,
                PUB_BASE_ARTICLE: product.pub || product.article.PUB,
                REMISE: remise,
                QTE: product.qte_unite_de_vente,
                QTE_UNITAIRE: product.qte_commande,
                CODGEN: product.article.CODGEN,
                DANS_PACK: isPack ? "O" : "N",
                MT_REMISE_PACK: isPack ? Pack.mt_remise : 0,
                QTE_PACK: isPack ? Pack.qte_commande : 0,
            });
        });

        return items;
    }, []);
};

const generateOrder = (current_cart) => {
    const { header, products, packs, _id, client } = current_cart;
    const cartData = getCartData(products, packs, client);

    return {
        header: { id: _id, ...generateOrderHeader(header, cartData) },
        items: generateOrderItems(products, packs),
    };
};
